import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Car, Bike, Users, MapPin, Star, Play, ArrowRight, Calendar, Check, Shield, Phone } from 'lucide-react'
import { useCurrencyStore, useLanguageStore } from '../../lib/store'
import { formatPrice } from '../../lib/utils'
import { vehiclesApi, toursApi, newsApi } from '../../lib/api'

interface Vehicle {
    id: number
    type: string
    brand: string
    model: string
    dailyRate: number
    location?: { address?: string }
    rating?: number
    reviewCount?: number
    translations?: { en?: { title?: string }; vi?: { title?: string } }
}

interface Tour {
    id: number
    type: string
    price: number
    duration: string
    translations?: { en?: { title?: string }; vi?: { title?: string } }
}

interface NewsArticle {
    id: number
    slug: string
    featured_image?: string
    published_at?: string
    title_en?: string
    title_vi?: string
}

interface Short {
    id: number
    thumbnail_url: string
    views: number
    title: string
}

export default function HomePage() {
    const { t } = useTranslation()
    const { currency } = useCurrencyStore()
    const { language } = useLanguageStore()

    const [featuredVehicles, setFeaturedVehicles] = useState<Vehicle[]>([])
    const [popularTours, setPopularTours] = useState<Tour[]>([])
    const [recentNews, setRecentNews] = useState<NewsArticle[]>([])
    const [latestShorts, setLatestShorts] = useState<Short[]>([])

    // Search form state with default dates
    const today = new Date().toISOString().split('T')[0]
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0]
    const [vehicleType, setVehicleType] = useState('')
    const [pickupDate, setPickupDate] = useState(today)
    const [returnDate, setReturnDate] = useState(tomorrow)

    useEffect(() => {
        // Fetch featured vehicles
        vehiclesApi.list({ limit: 4 }).then(res => {
            if (res.data?.vehicles) setFeaturedVehicles(res.data.vehicles)
        }).catch(console.error)

        // Fetch popular tours
        toursApi.list({ limit: 6 }).then(res => {
            if (res.data?.tours) setPopularTours(res.data.tours)
        }).catch(console.error)

        // Fetch recent news
        newsApi.list({ limit: 4 }).then(res => {
            if (res.data?.data) setRecentNews(res.data.data)
        }).catch(console.error)

        // Fetch shorts
        fetch('/api/shorts').then(r => r.json()).then(data => {
            if (data.success && data.data) setLatestShorts(data.data.slice(0, 5))
        }).catch(console.error)
    }, [])

    const getVehicleTitle = (v: Vehicle) => {
        if (language === 'en') return v.translations?.en?.title || `${v.brand} ${v.model}`
        return v.translations?.vi?.title || v.translations?.en?.title || `${v.brand} ${v.model}`
    }

    const getTourTitle = (t: Tour) => {
        if (language === 'en') return t.translations?.en?.title || 'Tour'
        return t.translations?.vi?.title || t.translations?.en?.title || 'Tour'
    }

    const getNewsTitle = (n: NewsArticle) => {
        return language === 'en' ? n.title_en : (n.title_vi || n.title_en) || 'Article'
    }

    return (
        <div className="min-h-screen">
            {/* Hero Section with Search */}
            <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white py-20 overflow-hidden">
                <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10"></div>
                <div className="container mx-auto px-4 relative">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                            {language === 'en' ? 'Explore Phú Quốc' : 'Khám Phá Phú Quốc'}
                            <span className="block text-yellow-400">Your Way</span>
                        </h1>
                        <p className="text-xl mb-8 opacity-90">
                            {language === 'en'
                                ? 'Rent motorcycles, cars & book tours from verified local owners'
                                : 'Thuê xe máy, ô tô & đặt tour từ chủ xe được xác thực'}
                        </p>

                        {/* Search Widget */}
                        <div className="bg-white rounded-xl p-4 md:p-6 shadow-2xl">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="md:col-span-1">
                                    <label className="block text-gray-700 text-sm font-medium mb-1 text-left">
                                        {language === 'en' ? 'Vehicle Type' : 'Loại xe'}
                                    </label>
                                    <select
                                        value={vehicleType}
                                        onChange={(e) => setVehicleType(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">{language === 'en' ? 'All Types' : 'Tất cả'}</option>
                                        <option value="motorcycle">{t('motorcycles')}</option>
                                        <option value="car">{t('cars')}</option>
                                        <option value="taxi">{t('taxis')}</option>
                                    </select>
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-gray-700 text-sm font-medium mb-1 text-left">
                                        {language === 'en' ? 'Pickup Date' : 'Ngày nhận'}
                                    </label>
                                    <input
                                        type="date"
                                        value={pickupDate}
                                        min={today}
                                        onChange={(e) => setPickupDate(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-gray-700 text-sm font-medium mb-1 text-left">
                                        {language === 'en' ? 'Return Date' : 'Ngày trả'}
                                    </label>
                                    <input
                                        type="date"
                                        value={returnDate}
                                        min={pickupDate}
                                        onChange={(e) => setReturnDate(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div className="md:col-span-1 flex items-end">
                                    <Link
                                        to={`/vehicles?type=${vehicleType}&pickup=${pickupDate}&return=${returnDate}`}
                                        className="w-full btn bg-orange-500 hover:bg-orange-600 text-white py-3 font-semibold"
                                    >
                                        <Search className="w-5 h-5 mr-2" />
                                        {t('search')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Vehicle Types */}
            <section className="py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-8 max-w-3xl mx-auto">
                        <Link to="/vehicles?type=motorcycle" className="card hover:shadow-lg transition group text-center p-3 sm:p-6">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-primary-200 transition">
                                <Bike className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
                            </div>
                            <h3 className="font-bold text-sm sm:text-base">{t('motorcycles')}</h3>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1 hidden sm:block">120+ {language === 'en' ? 'available' : 'xe'}</p>
                        </Link>

                        <Link to="/vehicles?type=car" className="card hover:shadow-lg transition group text-center p-3 sm:p-6">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-primary-200 transition">
                                <Car className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
                            </div>
                            <h3 className="font-bold text-sm sm:text-base">{t('cars')}</h3>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1 hidden sm:block">45+ {language === 'en' ? 'available' : 'xe'}</p>
                        </Link>

                        <Link to="/tours" className="card hover:shadow-lg transition group text-center p-3 sm:p-6">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-primary-200 transition">
                                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
                            </div>
                            <h3 className="font-bold text-sm sm:text-base">{t('tours')}</h3>
                            <p className="text-xs sm:text-sm text-gray-500 mt-1 hidden sm:block">25+ {language === 'en' ? 'experiences' : 'trải nghiệm'}</p>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Featured Vehicles */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-3xl font-bold">{language === 'en' ? 'Featured Vehicles' : 'Xe Nổi Bật'}</h2>
                            <p className="text-gray-600 mt-1">{language === 'en' ? 'Top-rated vehicles from verified owners' : 'Xe được đánh giá cao từ chủ xe xác thực'}</p>
                        </div>
                        <Link to="/vehicles" className="text-primary-600 hover:text-primary-700 font-semibold flex items-center">
                            {language === 'en' ? 'View All' : 'Xem tất cả'}
                            <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {featuredVehicles.map((vehicle) => (
                            <Link key={vehicle.id} to={`/vehicles/${vehicle.id}`} className="card hover:shadow-lg transition group">
                                <div className="aspect-[4/3] bg-gray-200 rounded-lg mb-4 overflow-hidden">
                                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-400">
                                        {vehicle.type === 'motorcycle' ? <Bike className="w-12 h-12" /> : <Car className="w-12 h-12" />}
                                    </div>
                                </div>
                                <h3 className="font-bold text-lg mb-1 group-hover:text-primary-600 transition line-clamp-1">{getVehicleTitle(vehicle)}</h3>
                                <div className="flex items-center text-sm text-gray-600 mb-2">
                                    <MapPin className="w-4 h-4 mr-1" />
                                    {vehicle.location?.address || 'Dương Đông'}
                                </div>
                                <div className="flex items-center mb-3">
                                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 mr-1" />
                                    <span className="font-semibold">{vehicle.rating?.toFixed(1) || '5.0'}</span>
                                    <span className="text-gray-500 text-sm ml-1">({vehicle.reviewCount || 0})</span>
                                </div>
                                <div className="flex items-center justify-between pt-3 border-t gap-2">
                                    <span className="text-lg font-bold text-primary-600 whitespace-nowrap">
                                        {formatPrice(vehicle.dailyRate, currency)}
                                    </span>
                                    <span className="text-gray-500 text-xs">/{language === 'en' ? 'day' : 'ngày'}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Latest Shorts */}
            <section className="py-16 bg-gray-900 text-white">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-3xl font-bold">{language === 'en' ? 'Latest Shorts' : 'Video Ngắn'}</h2>
                            <p className="text-gray-400 mt-1">{language === 'en' ? 'Discover the island through short videos' : 'Khám phá đảo qua video ngắn'}</p>
                        </div>
                        <Link to="/shorts" className="text-primary-400 hover:text-primary-300 font-semibold flex items-center">
                            {language === 'en' ? 'View All' : 'Xem tất cả'}
                            <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
                        {latestShorts.map((short) => (
                            <Link key={short.id} to={`/shorts?v=${short.id}`} className="flex-shrink-0 w-40 md:w-48 group">
                                <div className="aspect-[9/16] bg-gray-800 rounded-xl overflow-hidden relative">
                                    {short.thumbnail_url && (
                                        <img
                                            src={short.thumbnail_url}
                                            alt={short.title}
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                    )}
                                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 to-transparent">
                                        <Play className="w-12 h-12 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition" />
                                    </div>
                                    <div className="absolute bottom-2 left-2 right-2">
                                        <p className="text-white text-sm font-medium line-clamp-2">{short.title}</p>
                                        <p className="text-gray-300 text-xs mt-1">{(short.views / 1000).toFixed(1)}K views</p>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Popular Tours & Tickets */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-3xl font-bold">{language === 'en' ? 'Tours & Tickets' : 'Tour & Vé'}</h2>
                            <p className="text-gray-600 mt-1">{language === 'en' ? 'Book amazing experiences' : 'Đặt trải nghiệm tuyệt vời'}</p>
                        </div>
                        <Link to="/tours" className="text-primary-600 hover:text-primary-700 font-semibold flex items-center">
                            {language === 'en' ? 'View All' : 'Xem tất cả'}
                            <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {popularTours.map((tour) => (
                            <Link key={tour.id} to={`/tours/${tour.id}`} className="card p-4 hover:shadow-lg transition group">
                                <div className="aspect-square bg-gray-200 rounded-lg mb-3 overflow-hidden">
                                    <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-primary-400">
                                        <Calendar className="w-8 h-8" />
                                    </div>
                                </div>
                                <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary-600 transition">{getTourTitle(tour)}</h3>
                                <p className="text-xs text-gray-500 mt-1">{tour.duration}</p>
                                <p className="text-primary-600 font-bold mt-2">
                                    {formatPrice(tour.price, currency)}
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Recent News */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-3xl font-bold">{language === 'en' ? 'Travel Guides' : 'Cẩm Nang Du Lịch'}</h2>
                            <p className="text-gray-600 mt-1">{language === 'en' ? 'Tips and guides for your trip' : 'Mẹo và hướng dẫn cho chuyến đi'}</p>
                        </div>
                        <Link to="/news" className="text-primary-600 hover:text-primary-700 font-semibold flex items-center">
                            {language === 'en' ? 'View All' : 'Xem tất cả'}
                            <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {recentNews.map((article) => (
                            <Link key={article.id} to={`/news/${article.slug}`} className="card overflow-hidden hover:shadow-lg transition group">
                                <div className="aspect-video bg-gray-200">
                                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>
                                </div>
                                <div className="p-4">
                                    <p className="text-xs text-gray-500 mb-2">{article.published_at?.split('T')[0] || ''}</p>
                                    <h3 className="font-bold line-clamp-2 group-hover:text-primary-600 transition">{getNewsTitle(article)}</h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trust Indicators */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">{language === 'en' ? 'Why Choose Us' : 'Tại Sao Chọn Chúng Tôi'}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="font-bold mb-2">{language === 'en' ? 'Verified Owners' : 'Chủ Xe Xác Thực'}</h3>
                            <p className="text-gray-600 text-sm">{language === 'en' ? 'ID & phone verified' : 'Đã xác thực CCCD & SĐT'}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Shield className="w-8 h-8 text-blue-600" />
                            </div>
                            <h3 className="font-bold mb-2">{language === 'en' ? 'Secure Payment' : 'Thanh Toán An Toàn'}</h3>
                            <p className="text-gray-600 text-sm">{language === 'en' ? 'Escrow protection' : 'Bảo vệ qua escrow'}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Star className="w-8 h-8 text-yellow-600" />
                            </div>
                            <h3 className="font-bold mb-2">{language === 'en' ? 'Top Rated' : 'Đánh Giá Cao'}</h3>
                            <p className="text-gray-600 text-sm">{language === 'en' ? '5000+ reviews' : '5000+ đánh giá'}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Phone className="w-8 h-8 text-purple-600" />
                            </div>
                            <h3 className="font-bold mb-2">{language === 'en' ? '24/7 Support' : 'Hỗ Trợ 24/7'}</h3>
                            <p className="text-gray-600 text-sm">{language === 'en' ? 'Always here to help' : 'Luôn sẵn sàng hỗ trợ'}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="py-16 bg-primary-600 text-white">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <div>
                            <p className="text-4xl md:text-5xl font-bold mb-2">150+</p>
                            <p className="opacity-90">{language === 'en' ? 'Vehicles' : 'Phương tiện'}</p>
                        </div>
                        <div>
                            <p className="text-4xl md:text-5xl font-bold mb-2">50+</p>
                            <p className="opacity-90">{language === 'en' ? 'Verified Owners' : 'Chủ xe xác thực'}</p>
                        </div>
                        <div>
                            <p className="text-4xl md:text-5xl font-bold mb-2">5000+</p>
                            <p className="opacity-90">{language === 'en' ? 'Happy Customers' : 'Khách hài lòng'}</p>
                        </div>
                        <div>
                            <p className="text-4xl md:text-5xl font-bold mb-2">4.8</p>
                            <p className="opacity-90">{language === 'en' ? 'Average Rating' : 'Đánh giá TB'}</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}
